import {RefObject, useCallback, useEffect, useState} from 'react';

export const PLAYBACK_RATES = [1, 1.5, 2];

interface RateState {
    src: string;
    rate: number;
}

const applyRate = (audio: HTMLAudioElement, rate: number) => {
    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
    audio.preservesPitch = true;
};

export const usePlaybackSpeed = (audioRef: RefObject<HTMLAudioElement>, src: string) => {
    const [state, setState] = useState<RateState>({src, rate: 1});
    if (state.src !== src) {
        setState({src, rate: 1});
    }
    const rate = state.src === src ? state.rate : 1;

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const apply = () => applyRate(audio, rate);
        apply();
        audio.addEventListener('loadedmetadata', apply);
        audio.addEventListener('play', apply);
        return () => {
            audio.removeEventListener('loadedmetadata', apply);
            audio.removeEventListener('play', apply);
        };
    }, [audioRef, rate, src]);

    const cycleRate = useCallback(() => {
        setState(prev => {
            const current = prev.src === src ? prev.rate : 1;
            const next = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(current) + 1) % PLAYBACK_RATES.length];
            return {src, rate: next};
        });
    }, [src]);

    return {rate, cycleRate};
};
